import { reportTechnicalFailure } from './errorReport';
import { isMissingChunk } from './staleBuild';

/* =============================================================================
 * What a person is shown when something fails, and what the platform is told
 * -----------------------------------------------------------------------------
 * A site supervisor pressed "Take on" and read this, in red, inside the dialog:
 *
 *   Could not choose the best candidate function between:
 *   public.enrol_day_worker(p_identity_no => text, …) Try renaming the
 *   parameters or the function itself in the database so function overloading
 *   can be resolved (PGRST203)
 *
 * There is nothing in that they can do anything about, and it describes the
 * inside of a database they are not entitled to see. Meanwhile the one person
 * who could have fixed it was not told at all.
 *
 * -----------------------------------------------------------------------------
 * WHY THIS FILE USED TO DO THE OPPOSITE
 * -----------------------------------------------------------------------------
 * Deliberately, and for a good reason. supabase-js rejects with a
 * PostgrestError, which is a plain object rather than an Error, so
 * `caught instanceof Error ? caught.message : String(caught)` produced
 * "[object Object]" — a red banner that said nothing at all. The fix gathered
 * message, details, hint and code and showed all four, because Postgres puts
 * the useful half of a refusal in the hint far more often than people expect.
 *
 * Both readings were right about different audiences. The detail belongs to
 * whoever can act on it; the sentence belongs to whoever is standing at the
 * gate. This file now sends each to the right one.
 *
 * -----------------------------------------------------------------------------
 * THE RULE
 * -----------------------------------------------------------------------------
 * Show the message when somebody wrote it for a person. Otherwise show a
 * sentence about what to do next, with a reference, and send the rest to the
 * platform.
 *
 * SQLSTATE alone cannot decide it. This schema raises 523 exceptions and 84 of
 * them carry a deliberate errcode — 42501 for a refusal, 23503 for a dependency
 * — so a code says what kind of problem it is, not who the words were meant
 * for. A real foreign-key violation and a hand-raised one arrive as the same
 * code and read nothing alike.
 *
 * What separates them is the prose. Every `raise exception` in these migrations
 * writes a sentence: "They cannot have left before they arrived." Postgres
 * writes identifiers: `violates foreign key constraint "x_y_fkey"`. So a
 * message is shown when it reads like the first and hidden when it reads like
 * the second, and anything uncertain is hidden — the cost of hiding a good
 * sentence is that the operator reads it instead, and the cost of showing a bad
 * one is what this file exists to stop.
 * ========================================================================== */

interface LooksLikePostgrest {
  message?: unknown;
  details?: unknown;
  hint?: unknown;
  code?: unknown;
  status?: unknown;
  error_description?: unknown;
  error?: unknown;
  stack?: unknown;
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/** Everything that was thrown, pulled apart, for the platform's copy. */
export interface TechnicalDetail {
  message: string;
  code: string | null;
  detail: string | null;
  hint: string | null;
  stack: string | null;
}

export function technicalDetail(caught: unknown): TechnicalDetail {
  if (typeof caught === 'string') {
    return { message: caught, code: null, detail: null, hint: null, stack: null };
  }

  const shape = (caught ?? {}) as LooksLikePostgrest;

  const message =
    text(shape.message)
    ?? text(shape.error_description)
    /* `error` is sometimes a string and sometimes a nested object; only the
       string form is a message. */
    ?? text(shape.error)
    ?? safeJson(caught)
    ?? String(caught);

  return {
    message,
    code: text(shape.code) ?? (typeof shape.status === 'number' ? String(shape.status) : null),
    detail: text(shape.details),
    hint: text(shape.hint),
    stack: text(shape.stack),
  };
}

function safeJson(value: unknown): string | null {
  try {
    const json = JSON.stringify(value);
    return json && json !== '{}' ? json : null;
  } catch {
    return null; /* circular, or something exotic */
  }
}

/* -----------------------------------------------------------------------------
 * Postgres talking to a developer
 * -----------------------------------------------------------------------------
 * Each of these appears only in text the engine or the API layer generates.
 * None of them can occur in a sentence somebody wrote for a person, because
 * none of them is a thing anybody would write.
 * -------------------------------------------------------------------------- */
const ENGINE = [
  /violates (foreign key|check|not-null|unique|exclusion) constraint/i,
  /duplicate key value/i,
  /violates row-level security policy/i,
  /permission denied for (table|relation|function|schema|sequence)/i,
  /(function|relation|column|type|schema|operator) .* does not exist/i,
  /could not choose the best candidate/i,
  /syntax error at or near/i,
  /invalid input (syntax|value) for/i,
  /null value in column/i,
  /out of range/i,
  /deadlock detected/i,
  /canceling statement due to/i,
  /JWT|jwt expired|invalid claim/i,
  /failed to fetch|networkerror|load failed/i,
  /\bat [\w.<>]+ \(.*:\d+:\d+\)/,          /* a stack frame */
  /\b(public|app|auth|storage)\.\w+\s*\(/,  /* a schema-qualified call */
  /\bp_\w+\s*=>/,                            /* a named argument */
  /::(text|uuid|int|numeric|jsonb|timestamptz|geometry)/i,
  /\bPGRST\d+/,
];

/** SQLSTATEs and HTTP statuses that are never a sentence for a person. */
const NEVER_SHOWN = new Set([
  '42883', '42P01', '42703', '42601', '42P02',  /* undefined function / table / column / syntax */
  '08006', '08003', '57014', '53300', '40P01',  /* connection, cancelled, too many, deadlock */
  '500', '502', '503', '504',
  'PGRST100', 'PGRST200', 'PGRST201', 'PGRST202', 'PGRST203', 'PGRST204', 'PGRST301',
]);

/**
 * Whether this message was written by a person, for a person.
 *
 * Conservative by construction: it has to look like prose *and* carry no
 * fingerprint of the engine, and a message that fails either test is hidden.
 */
export function isWrittenForAPerson(message: string, code: string | null): boolean {
  if (!message) return false;
  if (code && NEVER_SHOWN.has(code)) return false;
  if (ENGINE.some((pattern) => pattern.test(message))) return false;

  /* Prose. Every exception raised in these migrations is a sentence with a full
     stop; the engine's are fragments full of identifiers. Length is capped
     because a genuinely long message is a dump, whatever else it looks like. */
  if (message.length > 240) return false;
  if (!/\s/.test(message)) return false;
  if (!/[.!?]$/.test(message)) return false;
  if (/[_{}<>|\\]/.test(message)) return false;

  return true;
}

/* -----------------------------------------------------------------------------
 * What to say instead
 * -----------------------------------------------------------------------------
 * Not "an unexpected error occurred". Each of these says what happened in terms
 * of the thing the reader was doing, and what to do next — and the last one
 * says plainly that somebody has been told, because the alternative is a person
 * deciding whether it is worth reporting.
 *
 * English here rather than through t(). This is reached from mutation handlers,
 * query callbacks and plain helpers that cannot call a hook; the interface
 * translates them at the point of display through errorText() below.
 * -------------------------------------------------------------------------- */
export type FailureKind =
  'credentials' | 'stale' | 'permission' | 'offline' | 'missing' | 'conflict' | 'fault';

export function classify(detail: TechnicalDetail): FailureKind {
  const { code, message } = detail;

  /* Before the network check, and that order is the whole point.
     -------------------------------------------------------------------------
     "Failed to fetch dynamically imported module" contains "failed to fetch",
     so it was being read as a dropped connection. The person was told to check
     their internet, which cannot help, and the platform's queue labelled it
     offline, which sends the operator looking at the wrong thing.

     A chunk that will not load is a build that has moved on: the tab is holding
     an index.html naming files a deploy has since renamed. lazyRoute reloads
     once on its own; this is the second failure, where the reload did not fix
     it — which in production means a deploy that is broken or half-published,
     and is worth an operator's attention under its own name. */
  if (isMissingChunk(message)) return 'stale';

  /* Before permission, and before anything else.
     -------------------------------------------------------------------------
     GoTrue writes "Invalid login credentials" — no full stop, so the prose test
     hides it, correctly, because it is not this application's sentence. But
     "something went wrong at our end" is a worse answer than the raw one: it
     tells somebody with a mistyped password to wait for a fix that is not
     coming. This is the one failure where the reader can put it right
     themselves, so it gets its own sentence. */
  if (/invalid login credentials|email not confirmed|invalid.*(token|otp)|user already registered/i.test(message)) {
    return 'credentials';
  }

  if (code === '42501' || code === '403' || /row-level security|permission denied/i.test(message)) {
    return 'permission';
  }
  if (/failed to fetch|networkerror|load failed|timeout|offline/i.test(message)) return 'offline';
  if (code === '404' || code === 'PGRST116') return 'missing';
  if (code === '23505' || code === '409') return 'conflict';

  return 'fault';
}

const SAY: Record<FailureKind, string> = {
  credentials: 'Those sign-in details were not recognised. Check the email and password and try again.',
  stale: 'The app has been updated since this page was opened. Reload the page to get the new version.',
  permission: 'You do not have permission to do that. Ask an administrator if you think you should.',
  offline: 'Could not reach the server. Check the connection and try again.',
  missing: 'That is no longer there. Refresh the page and try again.',
  conflict: 'That has already been recorded. Refresh the page to see it.',
  fault: 'Something went wrong at our end and this could not be completed. '
    + 'It has been reported. Quote reference {ref} if you need to ask about it.',
};

/**
 * The sentence to put in front of whoever is looking at the screen.
 *
 * A message somebody wrote for a person is returned as written. Anything else
 * is replaced, and the original goes to the platform operator with a reference
 * the person can quote.
 *
 * `doing` is what was being attempted, in the interface's own words — "enrol a
 * day worker", "save the shift times". It is the difference between an operator
 * reading a stack and an operator reading a report.
 */
export function errorMessage(caught: unknown, doing?: string): string {
  if (caught == null) return SAY.fault.replace(' Quote reference {ref} if you need to ask about it.', '');

  const detail = technicalDetail(caught);

  if (isWrittenForAPerson(detail.message, detail.code)) return detail.message;

  const kind = classify(detail);

  /* A mistyped password is not a fault and does not go to the platform. Repeated
     ones are worth knowing about and are already recorded, in login_events,
     where they belong: that table is about who tried to sign in, and this one is
     about what broke. */
  if (kind === 'credentials') return SAY.credentials;

  const reference = reportTechnicalFailure(detail, kind, doing);

  return SAY[kind].replace('{ref}', reference);
}
